<script setup lang="ts">
import { OrderBy } from '@/types/shared/filters'
import type { IHostedMinerFilters } from '@/types/hvl/filters'
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { Icon } from '@iconify/vue'
import { reactive, ref } from 'vue'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { useHostedMiners } from '@/composables/hvl/hosted-miners'
import { formatISODateToDateString } from '@/util/formatters'
import TableSearchFilter from '@/components/common/TableSearchFilter.vue'
import { SiteType } from '@/types/core/Site'
import type { ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import { useForemanApiKeys } from '@/composables/core/foreman-api-keys'
import { useOIDCStore } from '@/stores/oidc.store'
import type { IHostedMiner } from '@/types/hvl/HostedMiner'
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'

const { isHVLManagerUser } = useOIDCStore()

const { getHostedMinersQuery, refreshHostedMinersMutation, bulkDeleteHostedMinersMutation } = useHostedMiners()
const { getApiKeyQuery } = useForemanApiKeys()

const tableSelection = ref<IHostedMiner[]>([])
const handleTableSelect = (updatedSelection: IHostedMiner[]) => {
  tableSelection.value = updatedSelection
}

const filters = reactive<IHostedMinerFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[2],
  order_by: 'id',
  order_by_order: OrderBy.DESC,
  foreman_id: undefined,
  foreman_client: undefined,
  site_id: undefined,
  name: undefined,
  sn: undefined,
  model: undefined,
  mac_address: undefined,
  ip: undefined,
  active: undefined,
})

const {
  isPending: hostedMinersIsPending,
  isError: hostedMinersIsError,
  isRefetching: hostedMinersIsRefetching,
  data: hostedMinersData,
  error: hostedMinerError,
  refetch,
} = getHostedMinersQuery(filters)
const { data: apiKeyData } = getApiKeyQuery()
const { mutate: refreshMutate, isPending: isRefreshPending } = refreshHostedMinersMutation

const nameSearchSettings = {
  key: 'search',
  value: 'Search',
  type: 'search',
  placeholder: 'Name',
  style: 'width: 200px',
  onChange: (val: string) => {
    if (val == '') {
      filters.name = undefined
    } else {
      filters.name = val
    }
  },
}

const snSearchSettings = {
  key: 'search',
  value: 'Search',
  type: 'search',
  placeholder: 'Serial Number',
  style: 'width: 200px',
  onChange: (val: string) => {
    if (val == '') {
      filters.sn = undefined
    } else {
      filters.sn = val
    }
  },
}

const macAddressSearchSettings = {
  key: 'search',
  value: 'Search',
  type: 'search',
  placeholder: 'MAC Address',
  style: 'width: 200px',
  onChange: (val: string) => {
    if (val == '') {
      filters.mac_address = undefined
    } else {
      filters.mac_address = val
    }
  },
}

const modelSearchSettings = {
  key: 'search',
  value: 'Search',
  type: 'search',
  placeholder: 'Model',
  style: 'width: 200px',
  onChange: (val: string) => {
    if (val == '') {
      filters.model = undefined
    } else {
      filters.model = val
    }
  },
}

const onSiteFilterUpdate = (value?: ISiteIdentifier) => {
  filters.site_id = value?.site_id ?? undefined
}

const onRefresh = () => {
  refreshMutate()
}
</script>

<template>
  <ViewLayout>
    <template v-if="!hostedMinersIsError" #controls>
      <el-button
        v-if="isHVLManagerUser && tableSelection.length > 0"
        @click="
          DeleteConfirmationModal(
            'Delete Miners',
            `Are you sure to delete ${tableSelection.length} miners? This action cannot be undone.`,
            tableSelection.map((miner: IHostedMiner) => miner.id),
            bulkDeleteHostedMinersMutation.mutateAsync
          )
        "
        >Delete</el-button
      >
      <el-button v-if="apiKeyData" type="primary" :disabled="isRefreshPending" @click="onRefresh"
        >Refresh with Foreman</el-button
      >
    </template>
    <rl-error-result v-if="hostedMinersIsError && !hostedMinersIsRefetching" :error="hostedMinerError">
      <el-button @click="() => refetch()">
        <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
      </el-button>
    </rl-error-result>
    <template v-else>
      <div class="flex inline-flex gap-4 flex-wrap">
        <TableSearchFilter :search-settings="nameSearchSettings" :disabled="hostedMinersIsPending" />
        <TableSearchFilter :search-settings="snSearchSettings" :disabled="hostedMinersIsPending" />
        <TableSearchFilter :search-settings="macAddressSearchSettings" :disabled="hostedMinersIsPending" />
        <TableSearchFilter :search-settings="modelSearchSettings" :disabled="hostedMinersIsPending" />
        <RcSiteSelect
          placeholder="Site ID or Name"
          :type="SiteType.DataCenter"
          :disabled="hostedMinersIsPending"
          style="width: 200px"
          @update:model-value="onSiteFilterUpdate"
        />
        <el-select
          v-model="filters.active"
          style="width: 136px"
          placeholder="Active"
          clearable
          :disabled="hostedMinersIsPending"
        >
          <el-option label="Active" value="Active" />
          <el-option label="Inactive" value="Inactive" />
          <el-option label="All" value="All" />
        </el-select>
      </div>
      <el-table
        v-loading="hostedMinersIsPending"
        :data="hostedMinersData?.data"
        empty-text="No data"
        stripe
        @selection-change="handleTableSelect"
      >
        <el-table-column v-if="isHVLManagerUser" type="selection" width="55" />
        <el-table-column prop="name" label="Name" :min-width="224" />
        <el-table-column prop="sn" label="Serial Number" :min-width="176">
          <template #default="scope">
            {{ scope.row.sn == '' ? '-' : scope.row.sn.toUpperCase() }}
          </template>
        </el-table-column>
        <el-table-column prop="model" label="Model" :min-width="184" />
        <el-table-column prop="mac_address" label="MAC Address" :min-width="160">
          <template #default="scope">
            {{ scope.row.mac_address.toUpperCase() }}
          </template>
        </el-table-column>
        <el-table-column label="Foreman" :min-width="184">
          <template #default="scope">
            <p v-if="!scope.row.foreman_id && !scope.row.foreman_client">No Foreman data</p>
            <p v-if="scope.row.foreman_id">
              ID <b>{{ scope.row.foreman_id }}</b>
            </p>
            <p v-if="scope.row.foreman_client">
              Client <b>{{ scope.row.foreman_client }}</b>
            </p>
          </template>
        </el-table-column>
        <el-table-column prop="ip" label="IP" :min-width="100" />
        <el-table-column label="Active" :min-width="80">
          <template #default="scope">
            <Icon v-if="scope.row.active" icon="material-symbols:check" inline height="20" />
            <Icon v-if="!scope.row.active" icon="material-symbols:close" inline height="20" />
          </template>
        </el-table-column>
        <el-table-column prop="site.name" label="Site" :min-width="160" />
        <el-table-column label="Last Updated" :min-width="220">
          <template #default="scope">
            {{
              formatISODateToDateString(
                scope.row.updated_at ? scope.row.updated_at : scope.row.created_at,
                'MM/dd/yyyy HH:mm:ss ZZZZ'
              )
            }}
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="hostedMinersData && hostedMinersData.total_pages > 0"
        v-model:page-size="filters.per_page"
        v-model:current-page="filters.page"
        class="self-end"
        :page-sizes="DEFAULT_PAGE_SIZES"
        :page-count="hostedMinersData.total_pages"
        :total="hostedMinersData.total_records"
        :disabled="hostedMinersIsPending"
        :layout="DEFAULT_PAGINATION_LAYOUT"
        background
      />
    </template>
  </ViewLayout>
</template>
